import { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { OwnerType } from '@never2busy/shared';
import {
  keywordsApi,
  type Keyword,
  type CreateKeywordInput,
  type KeywordCategory,
} from '../../../api/endpoints/keywords';
import { PageHeader } from '../../../components/common/PageHeader';
import { UserSelect } from '../../../components/common/UserSelect';

const OWNER_OPTIONS: Array<{ label: string; value: OwnerType }> = [
  { label: 'Cuentas', value: OwnerType.ACCOUNT },
  { label: 'Contactos', value: OwnerType.PERSON },
  { label: 'Proyectos', value: OwnerType.PROJECT },
  { label: 'Actividades', value: OwnerType.ACTIVITY },
  { label: 'Ventas', value: OwnerType.SALE },
];

interface KeywordForm {
  text: string;
  shortText?: string;
  categoryId?: number | null;
  sort?: number;
}

interface CategoryForm {
  name: string;
  singleSelect?: boolean;
  securityScoped?: boolean;
  sort?: number;
}

export function KeywordsAdminPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.ACCOUNT);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Keyword | null>(null);
  const [form] = Form.useForm<KeywordForm>();

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<KeywordCategory | null>(null);
  const [catForm] = Form.useForm<CategoryForm>();

  const [accessKw, setAccessKw] = useState<Keyword | null>(null);
  const [accessUserId, setAccessUserId] = useState<number | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['keywords', 'list', ownerType],
    queryFn: () => keywordsApi.list(ownerType),
  });

  const categories = useQuery({
    queryKey: ['keywords', 'categories', ownerType],
    queryFn: () => keywordsApi.listCategories(ownerType),
  });

  const catName = new Map((categories.data ?? []).map((c) => [c.id, c.name]));
  const securityCatIds = new Set(
    (categories.data ?? []).filter((c) => c.securityScoped).map((c) => c.id),
  );

  const accessList = useQuery({
    queryKey: ['keywords', 'access', accessKw?.id],
    queryFn: () => keywordsApi.listAccess(accessKw!.id),
    enabled: !!accessKw,
  });

  const grantAccess = useMutation({
    mutationFn: () => keywordsApi.grantAccess(accessKw!.id, accessUserId!),
    onSuccess: () => {
      setAccessUserId(undefined);
      void queryClient.invalidateQueries({ queryKey: ['keywords', 'access', accessKw?.id] });
    },
    onError: () => message.error('No se pudo otorgar acceso'),
  });

  const revokeAccess = useMutation({
    mutationFn: (id: number) => keywordsApi.revokeAccess(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['keywords', 'access', accessKw?.id] });
    },
    onError: () => message.error('No se pudo quitar acceso'),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: KeywordForm) => {
      const payload = { ...values, categoryId: values.categoryId ?? undefined };
      if (editing) return keywordsApi.update(editing.id, payload);
      return keywordsApi.create({ ...payload, ownerType } as CreateKeywordInput);
    },
    onSuccess: () => {
      message.success(editing ? 'Etiqueta actualizada' : 'Etiqueta creada');
      void queryClient.invalidateQueries({ queryKey: ['keywords'] });
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: () => message.error('No se pudo guardar'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => keywordsApi.remove(id),
    onSuccess: () => {
      message.success('Etiqueta eliminada');
      void queryClient.invalidateQueries({ queryKey: ['keywords'] });
    },
    onError: () => message.error('No se pudo eliminar'),
  });

  const saveCat = useMutation({
    mutationFn: async (values: CategoryForm) => {
      if (editingCat) return keywordsApi.updateCategory(editingCat.id, values);
      return keywordsApi.createCategory({ ...values, ownerType });
    },
    onSuccess: () => {
      message.success(editingCat ? 'Categoría actualizada' : 'Categoría creada');
      void queryClient.invalidateQueries({ queryKey: ['keywords', 'categories'] });
      setCatModalOpen(false);
      setEditingCat(null);
      catForm.resetFields();
    },
    onError: () => message.error('No se pudo guardar la categoría'),
  });

  const removeCat = useMutation({
    mutationFn: (id: number) => keywordsApi.removeCategory(id),
    onSuccess: () => {
      message.success('Categoría inactivada');
      void queryClient.invalidateQueries({ queryKey: ['keywords'] });
    },
    onError: () => message.error('No se pudo inactivar'),
  });

  const openCreateKw = (): void => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };
  const openEditKw = (kw: Keyword): void => {
    setEditing(kw);
    form.setFieldsValue({
      text: kw.text,
      shortText: kw.shortText ?? undefined,
      categoryId: kw.categoryId ?? undefined,
      sort: kw.sort,
    });
    setModalOpen(true);
  };
  const openCreateCat = (): void => {
    setEditingCat(null);
    catForm.resetFields();
    setCatModalOpen(true);
  };
  const openEditCat = (c: KeywordCategory): void => {
    setEditingCat(c);
    catForm.setFieldsValue({
      name: c.name,
      singleSelect: c.singleSelect,
      securityScoped: c.securityScoped,
      sort: c.sort,
    });
    setCatModalOpen(true);
  };

  const catColumns: ColumnsType<KeywordCategory> = [
    {
      title: 'Categoría',
      dataIndex: 'name',
      render: (name: string, row) => (
        <Space>
          {name}
          {row.singleSelect && <Tag>una sola</Tag>}
          {row.securityScoped && (
            <Tag color="red" icon={<LockOutlined />}>
              seguridad
            </Tag>
          )}
        </Space>
      ),
    },
    { title: 'Orden', dataIndex: 'sort', width: 90, align: 'center' },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditCat(row)} />
          <Popconfirm
            title="¿Inactivar categoría?"
            description="Las etiquetas quedan sin categoría (no se borran)."
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeCat.mutate(row.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns: ColumnsType<Keyword> = [
    { title: 'Texto', dataIndex: 'text' },
    {
      title: 'Categoría',
      dataIndex: 'categoryId',
      render: (id: number | null) => (id != null ? (catName.get(id) ?? '—') : '—'),
    },
    { title: 'Corto', dataIndex: 'shortText' },
    { title: 'Orden', dataIndex: 'sort', width: 90, align: 'center' },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, row) => (
        <Space>
          {row.categoryId != null && securityCatIds.has(row.categoryId) && (
            <Button
              icon={<LockOutlined />}
              size="small"
              title="Quién puede ver"
              onClick={() => {
                setAccessKw(row);
                setAccessUserId(undefined);
              }}
            />
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditKw(row)} />
          <Popconfirm
            title="¿Eliminar etiqueta?"
            description="Si está asignada a registros, también se quitarán esas asignaciones."
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeMutation.mutate(row.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Etiquetas" crumbs={[{ title: 'Admin' }, { title: 'Etiquetas' }]} />

      <Space style={{ marginBottom: 16 }}>
        <Segmented
          options={OWNER_OPTIONS}
          value={ownerType}
          onChange={(v) => setOwnerType(v as OwnerType)}
        />
      </Space>

      <Card
        size="small"
        title="Categorías"
        style={{ marginBottom: 16 }}
        extra={
          <Button icon={<PlusOutlined />} size="small" onClick={openCreateCat}>
            Nueva categoría
          </Button>
        }
      >
        <Table<KeywordCategory>
          scroll={{ x: 'max-content' }}
          rowKey="id"
          size="small"
          loading={categories.isLoading}
          columns={catColumns}
          dataSource={categories.data ?? []}
          pagination={false}
        />
      </Card>

      <Card
        size="small"
        title="Etiquetas"
        extra={
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={openCreateKw}>
            Nueva etiqueta
          </Button>
        }
      >
        <Table<Keyword>
          scroll={{ x: 'max-content' }}
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={false}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar etiqueta' : 'Nueva etiqueta'}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form} onFinish={(values) => saveMutation.mutate(values)}>
          <Form.Item label="Texto" name="text" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="Categoría" name="categoryId">
            <Select
              allowClear
              placeholder="Sin categoría"
              options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item label="Texto corto" name="shortText">
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item label="Orden" name="sort" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={catModalOpen}
        title={editingCat ? 'Editar categoría' : 'Nueva categoría'}
        onCancel={() => setCatModalOpen(false)}
        onOk={() => catForm.submit()}
        confirmLoading={saveCat.isPending}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form
          layout="vertical"
          form={catForm}
          onFinish={(values) => saveCat.mutate(values)}
          initialValues={{ singleSelect: false, securityScoped: false, sort: 0 }}
        >
          <Form.Item label="Nombre" name="name" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input maxLength={60} />
          </Form.Item>
          <Form.Item
            label="Una sola etiqueta por registro"
            name="singleSelect"
            valuePropName="checked"
            extra="Al asignar una, se quita la otra de esta categoría."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Etiqueta de seguridad (restringe visibilidad)"
            name="securityScoped"
            valuePropName="checked"
            extra="Solo usuarios con acceso a estas etiquetas verán los registros marcados."
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Orden" name="sort">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!accessKw}
        title={`Acceso a "${accessKw?.text ?? ''}"`}
        onCancel={() => setAccessKw(null)}
        footer={null}
      >
        <p style={{ marginTop: 0, color: '#888' }}>
          Solo estos usuarios (además de ADMIN/GERENTE) verán los registros marcados con
          esta etiqueta de seguridad.
        </p>
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <UserSelect
              value={accessUserId}
              onChange={setAccessUserId}
              placeholder="Agregar usuario…"
            />
          </div>
          <Button
            type="primary"
            disabled={!accessUserId}
            loading={grantAccess.isPending}
            onClick={() => grantAccess.mutate()}
          >
            Otorgar
          </Button>
        </Space.Compact>
        <Table
          rowKey="id"
          size="small"
          loading={accessList.isLoading}
          pagination={false}
          dataSource={accessList.data ?? []}
          columns={[
            {
              title: 'Usuario',
              render: (_, row) => `${row.user.firstName} ${row.user.lastName}`,
            },
            {
              title: '',
              key: 'x',
              width: 60,
              align: 'right',
              render: (_, row) => (
                <Popconfirm
                  title="¿Quitar acceso?"
                  okText="Sí"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => revokeAccess.mutate(row.id)}
                >
                  <Button icon={<DeleteOutlined />} size="small" danger />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
